import { useDispatch } from 'react-redux';
import { ElementAction } from '../../elements/elementAction';
import { Functions } from '../../types/types';
import { setLoading } from '../Loader/loaderAction';
import { ContentPlanRootState as S } from '../../elements/contentPlan/store';
import { updatePlanningState } from './contentPlanningReducer';
import { Dispatch } from 'redux';
import { selectView, viewDataInSchema } from '../View/viewActions';
import { Metadata, Schema, Workspace } from '../../types/Metadata';
import { debounce } from '../../utils';
import { UserSetting, UserSettingProps } from '../../types/UserSetting';
import { API } from '../../api';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { View } from '../../types/View';
import { Tree } from '../../enums/Tree';
import { StatusCategories } from '../../types/StatusCategory';
import { AppRefId } from '../../utils/constants';
import { updateMetadataFormStateField } from '../MetadataForm/metadataFormReducer';
import { AdminDomain } from '../../enums/AdminDomain';
import { NodeType } from '../../enums/NodeType';

const loadPracticeCors =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const schemas = getState().planning?.selectedWorkspace?.schemas;
    if (!schemas?.length) return;
    const { getPracticeCors } = getAPI();
    const practiceCors = await getPracticeCors(schemas);
    dispatch(updatePlanningState({ practiceCors: practiceCors || {} }));
  };

export const selectWorkspace =
  (wid: string, vid?: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { selectedWorkspace } = getState().planning;
    const { form } = getState();
    const { schema: selectedSchema } = form;
    let w = selectedWorkspace;

    if (selectedWorkspace?.props?.refId != wid) {
      dispatch(setLoading(true));
      const { getWorkspace } = getAPI();
      w = await getWorkspace(wid);
      dispatch(updatePlanningState({ selectedWorkspace: w }));
      dispatch(loadPermissions());
      if (
        !!selectedSchema &&
        w?.schemas?.filter(ws => {
          return ws?.props?.type == selectedSchema?.props?.type;
        })?.length
      ) {
        const schemaMatch = w?.schemas?.filter(ws => {
          return ws?.props?.type == selectedSchema?.props?.type;
        })[0];
        if (
          !!schemaMatch &&
          schemaMatch?.props?.refId != selectedSchema?.props?.refId
        ) {
          dispatch(
            updateMetadataFormStateField({ ...form, schema: schemaMatch })
          );
        }
      }
    }
    dispatch(showView(w, vid));
  };

const showView =
  (w: Workspace, vid: string): ElementAction<Functions, S> =>
  async dispatch => {
    if (!w) return;
    const allViews = w.folders.reduce((views, folder) => {
      if (folder?.views?.length) return [...views, ...folder.views];
      return views;
    }, w.views || []);
    const view = allViews.filter(v => v.props.refId == vid)[0] || allViews[0];
    const schema = w.schemas.filter(s => s.props.refId == vid)[0];

    if (schema?.props.refId) {
      dispatch(viewDataInSchema(schema));
    } else {
      dispatch(selectView(view));
    }
  };

/**
 * When vid === undefined, do not reload view
 * When vid === null, reload currently showing view
 * Else show the view with given vid
 * If view with vid does not exists, show the first view in the workspace
 * @param wid
 * @param vid
 * @returns
 */
export const reloadWorkspace =
  (wid?: string, vid?: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning, view } = getState();
    const selectedWorkspaceId = planning.selectedWorkspace?.props?.refId;
    if (!wid) wid = selectedWorkspaceId;
    if (vid === null) {
      vid = view.selected?.props?.refId;
    }
    if (selectedWorkspaceId == wid) {
      const { getWorkspace } = getAPI();
      const w = await getWorkspace(wid);
      dispatch(updatePlanningState({ selectedWorkspace: w }));
      dispatch(loadPermissions());
      if (vid !== undefined) {
        dispatch(showView(w, vid));
      }
    }
  };

// export const updateWorkspaces =
//   (
//     workspaces: Workspace[],
//     schemaType?: string,
//     viewRefId?: string
//   ): ElementAction<Functions, S> =>
//   async (dispatch, getState, { updateUrl }) => {
//     const { planning, view } = getState();
//     const selectedWorkspace = workspaces.filter(
//       a => a.props?.refId == planning?.selectedWorkspace?.props?.refId
//     )[0];
//     if (selectedWorkspace) {
//       dispatch(selectView(null));
//       dispatch(updatePlanningState({ workspaces, selectedWorkspace }));
//       const allViews = getAllViews(selectedWorkspace);

//       if (viewRefId) {
//         let showView = allViews?.filter(v => v.props.refId == viewRefId)[0];
//         if (showView) {
//           dispatch(selectView(showView));
//           //updateUrl(selectedWorkspace, { v: showView });
//           return;
//         }
//       }
//       if (schemaType) {
//         const showSchema = selectedWorkspace.schemas?.filter(
//           s => s.props.type == schemaType
//         )[0];
//         if (showSchema) {
//           dispatch(viewDataInSchema(showSchema));
//           //updateUrl(selectedWorkspace, { v: showSchema });
//           return;
//         }
//       }
//       let currentView = allViews?.filter(
//         v => v.props?.refId == view?.selected?.props?.refId
//       )[0];

//       if (view?.selected?.schema?.props?.type) {
//         const schema = selectedWorkspace.schemas.filter(
//           s => s.props.type == view?.selected?.schema?.props?.type
//         )[0];
//         if (schema) {
//           dispatch(viewDataInSchema(schema));
//           //updateUrl(selectedWorkspace, { v: schema });
//           return;
//         }
//       }
//       const v = currentView || selectedWorkspace.views[0];
//       if (v) {
//         dispatch(selectView(v));
//         //updateUrl(selectedWorkspace, { v });
//       }
//     } else {
//       dispatch(updatePlanningState({ workspaces }));
//     }
//   };

export const loadPermissions =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { getPermissions } = getAPI();
    const permissions = await getPermissions();
    dispatch({ type: 'SET_PERMISSIONS', permissions });
  };

export const loadUserSettings =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { getUserSettings } = getAPI();
    const userSettings = await getUserSettings();
    dispatch(updatePlanningState({ userSettings }));
  };

const saveUserSettings = debounce(
  (
    { upsertUserSettings }: Pick<API, 'upsertUserSettings'>,
    refId: string,
    value: any,
    dispatch: any
  ) => {
    upsertUserSettings({
      refId,
      value
    }).finally(() => {
      dispatch(loadUserSettings());
    });
  },
  500
);

const getSrcRefId = (src: View.Setting | Schema | string): string => {
  if (!src) return null;
  if (typeof src == 'string') return src;
  return src.props?.refId;
};

export const setUserSetting =
  (
    src: View.Setting | Schema | string,
    value: UserSettingProps
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const srcRefId = getSrcRefId(src);
    if (!srcRefId) return;
    const userSettings = JSON.parse(
      JSON.stringify(getState().planning?.userSettings)
    );
    if (!userSettings) return;
    const userSetting =
      userSettings[srcRefId] || ({ props: {} } as UserSetting);
    const props = userSetting?.props;
    const currentValues = Object.keys(value).reduce((v, k) => {
      v[k] = props[k];
      return v;
    }, {});
    if (!deepEquals(currentValues, value)) {
      userSettings[srcRefId] = {
        ...userSetting,
        props: {
          ...props,
          ...value
        }
      };
      dispatch(updatePlanningState({ userSettings }));
      if (Object.values(AdminDomain).includes(srcRefId as any)) return;
      const isViewingAs = !!getState().view?.viewingAs?.props?.empCd;
      if (isViewingAs && isViewNode(src)) {
        return;
      }
      saveUserSettings(getAPI(), srcRefId, value, dispatch);
    }
  };

const isViewNode = (m: any): m is View.Setting =>
  typeof m == 'object' && m?.labels?.includes(NodeType.View);

const init =
  (
    wid: string,
    vid?: string,
    filters?: View.FiltersField[],
    sortBys?: View.SortByField[]
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    try {
      const planning = getState().planning;
      if (planning?.workspaces) return;
      dispatch(setLoading(true));
      const {
        getWorkspaces,
        getAllTree,
        getUserSettings,
        getTree,
        getContentTypeConfigurations,
        getDataMapping,
        upsertUserSettings
      } = getAPI();
      dispatch(loadPermissions());
      const [statusTree, contentTypeConfigurations, userSettings, workspaces] =
        await Promise.all([
          getTree({ tree: Tree.Status }),
          getContentTypeConfigurations(),
          getUserSettings(),
          getWorkspaces()
        ]);
      getDataMapping(null).then(dataMapping => {
        dispatch(updatePlanningState({ dataMapping }));
      });
      getAllTree(); // cache for use later
      const statusCategories = toStatusCategories(statusTree);
      if (vid && (filters || sortBys)) {
        const viewSetting = userSettings[vid] || ({ props: {} } as UserSetting);
        viewSetting.props.filters = filters;
        viewSetting.props.sortBys = sortBys;
        userSettings[vid] = viewSetting;
        upsertUserSettings({
          refId: vid,
          value: { filters, sortBys }
        });
      }
      dispatch(
        updatePlanningState({
          userSettings,
          statusCategories,
          contentTypeConfigurations
        })
      );

      if (wid) await dispatch(selectWorkspace(wid, vid));
      dispatch(updatePlanningState({ workspaces }));
    } finally {
      dispatch(setLoading(false));
    }
  };

const loadDataMapping =
  (params: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { getDataMapping } = getAPI();
    getDataMapping(params).then(dataMapping => {
      dispatch(updatePlanningState({ dataMapping }));
    });
  };

const toStatusCategories = (statusTree: Metadata[]): StatusCategories =>
  statusTree.reduce((cats, cat) => {
    const category = { ...cat, metadata: [] };
    cat.metadata?.forEach(s => {
      cats[s.props.id] = category;
    });
    return cats;
  }, {});

const loadWorkspaces =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const planning = getState().planning;
    if (planning?.workspaces) return;
    dispatch(setLoading(true));
    const { getWorkspaces, getUserSettings } = getAPI();
    dispatch(loadPermissions());
    const [userSettings, workspaces] = await Promise.all([
      getUserSettings(),
      getWorkspaces()
    ]);
    dispatch(updatePlanningState({ userSettings, workspaces }));
  };

const saveSelectedWorkspaceView =
  (
    selectedWorkspaceId: string,
    selectedViewId: string
  ): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    if (!selectedViewId) return;
    const { planning } = getState();
    if (!planning?.userSettings) return null;
    const userSetting = planning.userSettings[AppRefId];
    const homeWorkspaceId = userSetting?.props?.homeWorkspaceId;
    if (selectedWorkspaceId == homeWorkspaceId) {
      dispatch(
        setUserSetting(AppRefId, {
          selectedViewId
        })
      );
    }
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    loadWorkspaces: () => dispatch(loadWorkspaces()),
    loadDataMapping: (params: string) => dispatch(loadDataMapping(params)),
    loadPracticeCors: () => dispatch(loadPracticeCors()),
    init: (
      wid: string,
      viewRefId?: string,
      filters?: View.FiltersField[],
      sorts?: View.SortByField[]
    ) => dispatch(init(wid, viewRefId, filters, sorts)),
    saveSelectedWorkspaceView: (
      selectedWorkspaceId: string,
      selectedViewId?: string
    ) =>
      dispatch(saveSelectedWorkspaceView(selectedWorkspaceId, selectedViewId)),
    selectWorkspace: (w: string, viewRefId?: string) =>
      dispatch(selectWorkspace(w, viewRefId))
  };
};
